import { Check, Copy, Facebook, FileDown, Link2, Mail, MessageCircle, Share2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { shareUrl } from "@/components/shared/Seo";
import { posterUrl } from "@/lib/api";
import { copyText } from "@/lib/tickets";
import { fmtRange, fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const enc = encodeURIComponent;

/**
 * One share menu for an event: Facebook, WhatsApp, e-post, copy – and the phone's own share sheet where it exists.
 * Every link goes to the server-rendered share URL, so Facebook & co. get the real picture and title.
 */
export const ShareMenu = ({ ev, className, label = "Del", iconOnlyOnMobile = true, testId = "event-share" }) => {
  const [copied, setCopied] = useState(false);
  const url = shareUrl("arrangement", ev.id);
  const when = `${fmtRange(ev.date, ev.end_date)}${ev.start_time ? ` ${fmtTime(ev.start_time, ev.end_time)}` : ""}`;
  const text = `${ev.title} – ${when} på Halsnøy Kulturhus`;
  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  const copy = async () => {
    if (await copyText(url)) { setCopied(true); toast.success("Lenka er kopiert"); setTimeout(() => setCopied(false), 2000); } else toast.error("Kunne ikkje kopiere lenka");
  };
  const native = async () => {
    try { await navigator.share({ title: ev.title, text, url }); } catch (e) { if (e?.name !== "AbortError") toast.error("Kunne ikkje dele"); }
  };
  const links = [
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(`${text} ${url}`)}` },
    { id: "email", label: "E-post", icon: Mail, href: `mailto:?subject=${enc(ev.title)}&body=${enc(`${text}\n\n${url}`)}` },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Del arrangementet" className={cn("inline-flex items-center gap-2 transition-colors", className)} data-testid={`${testId}-button`}>
          <Share2 className="h-3.5 w-3.5" /> <span className={cn(iconOnlyOnMobile && "hidden sm:inline")}>{label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-[14px] bg-card p-1.5" data-testid={`${testId}-menu`}>
        {canNative && <DropdownMenuItem onSelect={native} className="h-10 cursor-pointer rounded-[9px] text-[14px]" data-testid={`${testId}-native`}><Share2 className="h-4 w-4" /> Del via …</DropdownMenuItem>}
        {links.map((l) => (
          <DropdownMenuItem key={l.id} asChild className="h-10 cursor-pointer rounded-[9px] text-[14px]" data-testid={`${testId}-${l.id}`}>
            <a href={l.href} target={l.id === "email" ? undefined : "_blank"} rel="noopener noreferrer"><l.icon className="h-4 w-4" /> {l.label}</a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="h-10 cursor-pointer rounded-[9px] text-[14px]" data-testid={`${testId}-poster`}>
          <a href={posterUrl(ev.id, "A4")} target="_blank" rel="noopener noreferrer"><FileDown className="h-4 w-4" /> Plakat (PDF, A4)</a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); copy(); }} className="h-10 cursor-pointer rounded-[9px] text-[14px]" data-testid={`${testId}-copy`}>
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Kopiert" : "Kopier lenkje"}
        </DropdownMenuItem>
        <p className="flex items-center gap-1.5 px-2 pb-1 pt-1.5 text-[11.5px] text-muted-foreground"><Link2 className="h-3 w-3" /> Lenka viser bilete og tittel når ho vert delt.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
